import { NetworkBase, NetworkBinanceSmartChain, NetworkCronos, NetworkEthereum, NetworkSolana, NetworkTron } from "@token-icons/react";
import { erc20Abi, parseAbi } from "viem";
import { readContract } from "@wagmi/core";
import { mainnet, base, polygon, arbitrum, optimism, bsc, avalanche, fantom } from "wagmi/chains";
import { config } from "index";
import contractABI from "../assets/contractABI.json"

export const getChainIcon = (type = '') => {
    switch (type.toLowerCase()) {
      case "solana":
        return <NetworkSolana size={20}  color="#00ffad"/>;
      case "ethereum":
        return <NetworkEthereum size={20} color="#3c3c3d" />;
      case "base":
        return <NetworkBase size={20}  color="#0052ff"/>;
      case "bsc":
        return <NetworkBinanceSmartChain size={20} color="#F3BA2F" />;
      case "ton":
        return <NetworkTron size={20} color="#0088CC"/>;
      case "cronos":
        return <NetworkCronos size={20} color="white"/>;
      case "sonic":
        return <img src="https://dd.dexscreener.com/ds-data/chains/sonic.png" width={20} height={20} alt="Sonic" />;
      case "pulsechain":
        return <img src="https://dd.dexscreener.com/ds-data/chains/pulsechain.png" width={20} height={20} alt="pulsechain" />;
      default:
        return null;
    }
  };

  export const shortenAddress = (address) => {
    return address ? `${address.slice(0, 5)}...${address.slice(-4)}` : "N/A";
  };

  export const formatCurrency = (value) => {
    if (!value || isNaN(value)) return "N/A";
    
    const absValue = Math.abs(value);
    
    if (absValue >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (absValue >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (absValue >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
  
    return `$${value}`;
  };
  

  export const formatTimeDifference = (timestamp) => {
    if(timestamp == 0){
      return "sometime";
    }
    const now = new Date();
    const targetDate = new Date(timestamp * 1000);
  
    let years = now.getFullYear() - targetDate.getFullYear();
    let months = now.getMonth() - targetDate.getMonth();
    let days = now.getDate() - targetDate.getDate();
    let hours = now.getHours() - targetDate.getHours();
    let minutes = now.getMinutes() - targetDate.getMinutes();

    // Adjust for negative values

    if (minutes < 0) {
      hours -= 1;
      minutes += 60;
    }
    if (hours < 0) {
      days -= 1;
      hours += 24;
    }
    if (days < 0) {
      months -= 1;
      days += new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

  
    return `${years > 0 ? years + " yr " : ""}${months > 0 ? months + " mo " : ""}${days > 0 ? days + " d " : ""}${hours > 0 ? hours + " hr " : ""}${minutes > 0 ? minutes + " min " : ""}`.trim();
  };

// Create a mapping of chain IDs to chain objects from Wagmi
const chainMap = {
  [mainnet.id]: mainnet.name.toLowerCase(),
  [base.id]: base.name.toLowerCase(),
  [polygon.id]: polygon.name.toLowerCase(),
  [arbitrum.id]: arbitrum.name.toLowerCase(),
  [optimism.id]: optimism.name.toLowerCase(),
  [bsc.id]: bsc.name.toLowerCase(),
  [avalanche.id]: avalanche.name.toLowerCase(),
  [fantom.id]: fantom.name.toLowerCase(),
};

// Reverse mapping (name → id)
const reverseChainMap = Object.fromEntries(
  Object.entries(chainMap).map(([id, name]) => [name, Number(id)])
);

/**
 * Get the chain name from its ID or the chain ID from its name.
 * @param {number | bigint | string} value - Chain ID (number/bigint) or Chain Name (string).
 * @returns {string | number} - Chain Name if ID is provided, or Chain ID if Name is provided.
 */
export function getChainName(value) {
  if (typeof value === "string") {
    return reverseChainMap[value.toLowerCase()] || `Unknown Chain Name (${value})`;
  } else {
    const id = Number(value); // Convert bigint to number if necessary
    return chainMap[id] || `Unknown Chain ID (${id})`;
  }
}

// Define a mapping of chains to their respective DEX addresses
const dexAddressMap = {
  [mainnet.id]: {
    uniswap: "0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f", // UniswapV2 Factory (Mainnet)
    sushiswap: "0xC0a47DFe034B400B47bDaD5FecDa2621de6c4d95",
  },
  [polygon.id]: {
    quickswap: "0x5757371414417b8C6CAad45bAEF941aBc7d3Ab32",
    sushiswap: "0xc35DADB65012eC5796536bD9864eD8773aBc74C4",
  },
  [base.id]: {
    uniswap: "0x33128a8fC17869897dcE68Ed026d694621f6FDfD", // Add Uniswap address on Base
    sushiswap: "0x85CD07Ea01423b1E937929B44E4Ad8c40BbB5E71", // Add SushiSwap address on Base
  },
  [arbitrum.id]: {
    uniswap: "0x...",
    sushiswap: "0x...",
    balancer: "0x...",
  },
  [bsc.id]: {
    pancakeswap: "0xBCfCcbde45cE874adCB698cC183deBcF17952812",
  },
  [avalanche.id]: {
    traderjoe: "0x9Ad6C38BE94206cA50bb0d90783181662f0Cfa10",
  },
  [fantom.id]: {
    spookyswap: "0x2b2929E785374c651a81A63878Ab22742656DcDd",
  },
};

/**
 * Get the DEX name and chain ID from an address.
 * @param {string} address - The DEX contract address.
 * @returns {object | string} - Object with { dexName, chainId } or an error message.
 */


export function getDexName(chainId, value) {
  const id = Number(chainId); // Convert bigint to number if needed
  const dexesOnChain = dexAddressMap[id];

  if (!dexesOnChain) return `Unknown Chain ID (${id})`;

  const lowerValue = value.toLowerCase();

  // If the input is a DEX name, return its address
  if (dexesOnChain[lowerValue]) {
    return dexesOnChain[lowerValue];
  }

  // If the input is an address, return its DEX name
  const dexName = Object.entries(dexesOnChain).find(([_, addr]) => addr.toLowerCase() === lowerValue);
  if (dexName) return dexName[0];

  return `Unknown DEX (${value}) on Chain ${id}`;
}



export function calculateTVL(targetTVL, currentTVL) {
  
  
  
  if (targetTVL === undefined || currentTVL === undefined) {
    return "0.00";
  }

  let target = Number(targetTVL);
  let current = Number(currentTVL);
  
  if (isNaN(target) || isNaN(current) || target === 0) {
    return "0.00"; // Prevent division by zero or invalid numbers
  }

  let result = (current / target) * 100;
  result = Math.min(result, 100); // Ensure max is 100

  
  
  

  return result.toFixed(2);
}
// export function getTokenDecimals(tokenAddress) {
//   const { data: decimals, isLoading } = useReadContract({
//     address: tokenAddress,
//     abi: erc20Abi,
//     functionName: "decimals",
//   });

//   return { decimals, isLoading };
// }
export const getTokenDecimals = async (tokenAddress,chainId) => {
  if (!tokenAddress) {
    console.error("Invalid token address:", tokenAddress);
    return 18; // Default to 18 decimals if no token address
  }

  try {
    const decimals = await readContract(config,{
      abi: erc20Abi, // Use viem's built-in ERC-20 ABI
      address: tokenAddress,
      functionName: "decimals",
      chainId: base.id,
    });

    return Number(decimals); // Convert BigInt to number
  } catch (error) {
    console.error(`Failed to fetch decimals for ${tokenAddress}:`, error);
    return 18; // Default to 18 in case of failure
  }
};

export const getCurrentFee = async () => {

  try {
    const fee = await readContract(config,{
      abi: contractABI, // Use viem's built-in ERC-20 ABI
      address: process.env.REACT_APP_PROXY_CONTRACT_ADDRESS,
      functionName: "getRequiredFee",
      chainId: base.id,
    });

    return fee; // Convert BigInt to number
  } catch (error) {
    console.error(`Failed to fetch fee :`, error);
    return 18; // Default to 18 in case of failure
  }
};

// Helper function to convert number to subscript
const toSubscript = (num) => {
  const subscriptDigits = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
  return num.toString().split('').map(digit => subscriptDigits[parseInt(digit)]).join('');
};

// Format price for display with subscript zeros
export const formatPriceDisplay = (price) => {
  if (price === 0) return '$0.00';
  
  // Convert to string and find the first non-zero digit after decimal
  const strValue = price.toString();
  const decimalPart = strValue.split('.')[1] || '';
  
  // Count leading zeros
  let zeroCount = 0;
  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] === '0') {
      zeroCount++;
    } else {
      break;
    }
  }
  
  // If there are leading zeros, use the special format
  if (zeroCount > 0) {
    // Get the significant digits (everything after the leading zeros)
    const significantDigits = decimalPart.substring(zeroCount);
    
    // Create subscript number for zero count
    const subscriptZeroCount = toSubscript(zeroCount);
    
    return `$0.0${subscriptZeroCount}${significantDigits}`;
  }
  
  // For larger numbers, use standard formatting
  return `$${parseFloat(price).toFixed(4)}`;
};
